import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { LoadingSpinner, AuthErrorScreen } from '../components/feedback';
import { useAuthErrorInfo } from '../hooks/useAuthErrorInfo';
import { getInvitationParams } from '../helpers/auth_invitation';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
  const location = useLocation();
  // In the real app tree, App.tsx already renders AuthErrorScreen in place
  // of the Sidebar + routes before PrivateRoute ever mounts, so this is
  // primarily a defensive guard (kept here too in case PrivateRoute is ever
  // reached directly, e.g. in isolation or tests).
  const authErrorInfo = useAuthErrorInfo();

  useEffect(() => {
    // Only auto-redirect to Auth0 when the user is simply logged out.
    // If Auth0 came back with an error (wrong credentials, a blocked SSO
    // connection, a broken token exchange, etc.) `isAuthenticated` is also
    // false, so redirecting unconditionally here re-sends the user straight
    // back into the same failing flow on every render — an infinite
    // redirect loop. Bailing out on `authErrorInfo` breaks that loop and
    // lets the error screen below take over instead.
    if (!isLoading && !isAuthenticated && !authErrorInfo) {
      // An Auth0 organization invitation link lands here carrying these two
      // params (FR-023); relaying them resumes Universal Login's
      // accept-invitation flow instead of a plain login.
      const invitationParams = getInvitationParams(location.search);
      loginWithRedirect(invitationParams ? { authorizationParams: invitationParams } : undefined);
    }
  }, [isLoading, isAuthenticated, authErrorInfo, loginWithRedirect, location.search]);

  if (authErrorInfo) {
    return (
      <AuthErrorScreen
        errorInfo={authErrorInfo}
        // Explicit, user-initiated retry only — never automatic. For token
        // exchange failures specifically, retrying `loginWithRedirect()`
        // is what causes the loop, so that case is reset via `onReset`
        // instead and never offered a "Try Again" retry.
        onRetry={() => loginWithRedirect()}
        onReset={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      />
    );
  }

  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner size="lg" label="Redirecting to login..." />
      </div>
    );
  }

  return children;
};

export default PrivateRoute;
